import React, { useState } from "react";
import { Syringe, Info } from "lucide-react";
import { exportCalculatorPDF } from "../../lib/pdfExport";
import { SavePdfButton } from "./SavePdfButton";

// ICR — Insulin-to-Carb Ratio (rule of 500)
//   ICR (g of carbs per 1 unit) = 500 / TDD
//   Bolus units = grams of carbs / ICR
export default function ICRCalc({ lang }) {
  const [tdd, setTdd] = useState("40");
  const [carbs, setCarbs] = useState("60");

  const tddN = parseFloat(tdd);
  const carbsN = parseFloat(carbs);
  const validTdd = !isNaN(tddN) && tddN >= 5 && tddN <= 200;
  const validCarbs = !isNaN(carbsN) && carbsN >= 0 && carbsN <= 300;

  const icr = validTdd ? Math.round((500 / tddN) * 10) / 10 : null;
  const bolus = icr && validCarbs ? Math.round((carbsN / icr) * 10) / 10 : null;

  return (
    <section className="card-clinic !p-7" data-testid="icr-calculator">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-sapphire-50 text-sapphire-700 flex items-center justify-center">
          <Syringe className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-surface-ink">
            {lang === "et"
              ? "ICR — insuliini-süsivesikute suhe"
              : lang === "ru"
              ? "ICR — соотношение инсулин/углеводы"
              : "ICR — insulin-to-carb ratio"}
          </h2>
          <p className="text-[15px] text-surface-inkSoft">
            {lang === "et"
              ? "1 TÜ kiiretoimelist insuliini iga X g süsivesiku kohta. Arvutus reegli 500 järgi."
              : lang === "ru"
              ? "1 ед быстродействующего инсулина на каждые X г углеводов. По правилу 500."
              : "1 unit of rapid-acting insulin per X g of carbs. Based on the 500 rule."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[15px] font-medium text-surface-ink mb-1.5">
            {lang === "et"
              ? "Päevane insuliini koguannus (TDD), TÜ"
              : lang === "ru"
              ? "Суточная доза инсулина (TDD), ед"
              : "Total daily insulin dose (TDD), units"}
          </label>
          <input
            type="number"
            step="0.5"
            min="5"
            max="200"
            value={tdd}
            onChange={(e) => setTdd(e.target.value)}
            className="input w-full text-[16px] font-medium"
            data-testid="icr-tdd-input"
          />
          <p className="text-[14px] text-surface-inkSoft mt-1.5">
            {lang === "et"
              ? "TDD = baasinsuliin + kõik söögikordade boolused 24 h jooksul"
              : lang === "ru"
              ? "TDD = базальный инсулин + все болюсы за 24 ч"
              : "TDD = basal insulin + all meal boluses over 24 h"}
          </p>
        </div>
        <div>
          <label className="block text-[15px] font-medium text-surface-ink mb-1.5">
            {lang === "et"
              ? "Söögikorra süsivesikud (g)"
              : lang === "ru"
              ? "Углеводы в приёме пищи (г)"
              : "Carbs in meal (g)"}
          </label>
          <input
            type="number"
            min="0"
            max="300"
            value={carbs}
            onChange={(e) => setCarbs(e.target.value)}
            className="input w-full text-[16px] font-medium"
            data-testid="icr-carbs-input"
          />
          <p className="text-[14px] text-surface-inkSoft mt-1.5">
            {lang === "et"
              ? "Vali, kui palju süsivesikuid kavatsed süüa — arvutame booluse"
              : lang === "ru"
              ? "Введите количество углеводов, рассчитаем болюс"
              : "Enter carbs you plan to eat — we compute the bolus"}
          </p>
        </div>
      </div>

      {icr && (
        <div
          className="mt-5 rounded-xl border border-sapphire-300 bg-sapphire-50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4"
          data-testid="icr-result"
        >
          <div>
            <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">ICR</div>
            <div className="text-3xl font-bold text-surface-ink">
              1 : {icr}
            </div>
            <div className="text-[14px] font-semibold mt-1 text-sapphire-700">
              {lang === "et"
                ? `1 TÜ insuliini katab ~${icr} g süsivesikuid`
                : lang === "ru"
                ? `1 ед инсулина покрывает ~${icr} г углеводов`
                : `1 unit covers ~${icr} g of carbs`}
            </div>
          </div>
          {bolus !== null && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">
                {lang === "et"
                  ? "Soovituslik söögikorra boolus"
                  : lang === "ru"
                  ? "Рекомендуемый болюс на еду"
                  : "Suggested meal bolus"}
              </div>
              <div className="text-3xl font-bold text-surface-ink">
                {bolus} <span className="text-base font-medium">TÜ</span>
              </div>
              <div className="text-[14px] text-surface-inkSoft mt-1">
                {lang === "et"
                  ? `${carbsN} g ÷ ${icr} g/TÜ`
                  : lang === "ru"
                  ? `${carbsN} г ÷ ${icr} г/ед`
                  : `${carbsN} g ÷ ${icr} g/U`}
              </div>
            </div>
          )}
        </div>
      )}

      {icr && (
        <SavePdfButton
          lang={lang}
          testId="icr-save-pdf"
          onClick={() =>
            exportCalculatorPDF({
              lang,
              title:
                lang === "et"
                  ? "ICR — insuliini-süsivesikute suhe"
                  : lang === "ru"
                  ? "ICR — соотношение инсулин/углеводы"
                  : "ICR — insulin-to-carb ratio",
              inputs: [
                {
                  label:
                    lang === "et"
                      ? "Päevane insuliini koguannus (TDD)"
                      : lang === "ru"
                      ? "Суточная доза инсулина (TDD)"
                      : "Total daily insulin dose (TDD)",
                  value: `${tddN} TÜ`,
                },
                ...(validCarbs
                  ? [
                      {
                        label:
                          lang === "et"
                            ? "Söögikorra süsivesikud"
                            : lang === "ru"
                            ? "Углеводы в приёме пищи"
                            : "Carbs in meal",
                        value: `${carbsN} g`,
                      },
                    ]
                  : []),
              ],
              results: [
                {
                  label: "ICR",
                  value: `1 : ${icr}`,
                  hint:
                    lang === "et"
                      ? `1 TÜ insuliini katab ~${icr} g süsivesikuid`
                      : lang === "ru"
                      ? `1 ед инсулина покрывает ~${icr} г углеводов`
                      : `1 unit covers ~${icr} g of carbs`,
                },
                ...(bolus !== null
                  ? [
                      {
                        label:
                          lang === "et"
                            ? "Soovituslik söögikorra boolus"
                            : lang === "ru"
                            ? "Рекомендуемый болюс на еду"
                            : "Suggested meal bolus",
                        value: `${bolus} TÜ`,
                        hint: `${carbsN} g / ${icr} g/TÜ`,
                      },
                    ]
                  : []),
              ],
              note:
                lang === "et"
                  ? "Reegel 500 on lähtepunkt kiiretoimelisele insuliinile. Tegelik ICR võib varieeruda hommiku, õhtu ja pingutuse vahel. Kohanda koos diabeediõe või endokrinoloogiga."
                  : lang === "ru"
                  ? "Правило 500 — стартовая точка для быстродействующего инсулина. Корректируйте только вместе с врачом."
                  : "The 500 rule is a starting estimate for rapid-acting insulin. Adjust only with your diabetes nurse / endocrinologist.",
            })
          }
        />
      )}

      <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-300/60 p-4 text-[14px] leading-relaxed text-surface-ink">
        <Info className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <p>
          {lang === "et"
            ? "Reegel 500 on lähtepunkt kiiretoimelisele insuliinile (lispro, aspart, glulisiin). Tegelik ICR võib varieeruda söögikordade lõikes (hommik vs. õhtu), pingutuse ja menstruaaltsükli järgi. Kohanda ICR-i koos diabeediõe/endokrinoloogiga vastavalt veresuhkru mõõtmistele."
            : lang === "ru"
            ? "Правило 500 — стартовая точка для быстродействующего инсулина. Реальный ICR может различаться по времени суток и активности. Корректируйте только вместе с врачом."
            : "The 500 rule is a starting estimate for rapid-acting insulin. Real ICR varies by meal, activity and cycle. Adjust only with your diabetes nurse / endocrinologist."}
        </p>
      </div>
    </section>
  );
}
